import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { type ExportConfig, type ReportType } from '../types';
import { useDashboardDataContext } from '../../../helpers/hooks/useDashboardDataContext';

// Hook for managing export configuration and options
export const useExportConfig = () => {
  const routeMatch = useParams();
  const { dashboardWidgets } = useDashboardDataContext();

  const [exportConfig, setExportConfig] = useState<ExportConfig>({
    isLoaded: false,
    isLoading: false,
    isError: false,
    data: []
  });

  // Reset export config when route or dashboard widgets change
  useEffect(() => {
    setExportConfig({
      isLoaded: false,
      isLoading: false,
      isError: false,
      data: []
    });
  }, [routeMatch?.dashboard, routeMatch?.widget, dashboardWidgets]);

  const getReportTypesForExport = (): ReportType[] => {
    try {
      // Get current widget from route params
      const widget = routeMatch?.widget;

      if (!widget) {
        console.warn('Widget context not available', { widget, routeMatch });
        return [];
      }

      // Check if dashboardWidgets is available and populated
      if (!dashboardWidgets || dashboardWidgets.length === 0) {
        console.warn('Dashboard widgets not loaded yet', { dashboardWidgets });
        return [];
      }

      // Find the current widget in the already-fetched dashboard data
      const currentWidget = dashboardWidgets?.find((w: any) => w.url === widget);

      if (!currentWidget) {
        console.warn('No widget data found for:', widget, {
          availableWidgets: dashboardWidgets?.map((w: any) => w.url)
        });
        return [];
      }

      // Get report variations from the widget data
      const reportDownloadVariations = currentWidget.report_download_variations || [];
      const widgetUrl = currentWidget.url || widget;

      // Only return variations if reports are enabled for this widget
      if (!currentWidget.is_report_enabled) {
        console.warn('Reports not enabled for widget:', widget);
        return [];
      }

      if (reportDownloadVariations.length === 0) {
        console.warn('No report download variations found for widget:', widget);
        return [];
      }

      // Transform the variations to the expected format and sort them
      const result = reportDownloadVariations
        .map((variation: { name: string; id: string }) => ({
          name: variation.name,
          url: variation.id, // The variation's id becomes the url parameter
          widgetUrl: widgetUrl // The widget's url field
        }))
        .sort((a: { name: string }, b: { name: string }) => {
          // Sort alphabetically by name for consistent order
          return a.name.localeCompare(b.name);
        });
      return result;
    } catch (error) {
      console.error('Failed to get report download variations:', error);
      // Set error state properly instead of just returning empty array
      setExportConfig((prev) => ({
        ...prev,
        isError: true,
        isLoading: false
      }));
      return [];
    }
  };

  const loadExportOptions = () => {
    if (exportConfig.isLoading) return;

    // If already loaded and has data, don't reload
    if (exportConfig.isLoaded && exportConfig.data.length > 0) return;

    setExportConfig({
      isLoaded: false,
      isLoading: true,
      isError: false,
      data: []
    });

    // Use setTimeout to avoid blocking UI
    setTimeout(() => {
      try {
        const data = getReportTypesForExport();

        // If no data and dashboardWidgets isn't loaded yet, retry after a short delay
        if (data.length === 0 && (!dashboardWidgets || dashboardWidgets.length === 0)) {
          console.log('Dashboard widgets not ready, retrying in 500ms...');
          setTimeout(() => {
            try {
              const retryData = getReportTypesForExport();
              setExportConfig({
                isLoaded: true,
                isLoading: false,
                isError: retryData.length === 0,
                data: retryData
              });
            } catch (retryError) {
              console.error('Retry failed:', retryError);
              setExportConfig({
                isLoaded: true,
                isLoading: false,
                isError: true,
                data: []
              });
            }
          }, 500);
          return;
        }

        setExportConfig({
          isLoaded: true,
          isLoading: false,
          isError: false,
          data
        });
      } catch (error) {
        console.error('Error getting export options:', error);
        setExportConfig({
          isLoaded: true,
          isLoading: false,
          isError: true,
          data: []
        });
      }
    }, 100);
  };

  const refreshExportOptions = () => {
    console.log('Manually refreshing export options...');
    setExportConfig({
      isLoaded: false,
      isLoading: true,
      isError: false,
      data: []
    });

    setTimeout(() => {
      try {
        const data = getReportTypesForExport();
        setExportConfig({
          isLoaded: true,
          isLoading: false,
          isError: data.length === 0,
          data
        });
      } catch (error) {
        console.error('Manual refresh failed:', error);
        setExportConfig({
          isLoaded: true,
          isLoading: false,
          isError: true,
          data: []
        });
      }
    }, 100);
  };

  return {
    exportConfig,
    dashboardWidgets,
    loadExportOptions,
    refreshExportOptions
  };
};
